/**
 * Media & Text Modal Section — modal frontend behavior.
 */
import {
	lockBackgroundScroll,
	unlockBackgroundScroll,
} from '../shared/background-scroll-lock';
import {
	MODAL_OPEN_CLASS,
	animateModalClose,
	animateModalOpen,
	resetModalScroll,
} from '../shared/modal-animation';
import { MODAL_BODY_CLASS } from './modal-constants';
import { THEME_COLORS } from './constants';
import {
	decodeHtmlEntities,
	findCardModalData,
	getModalChromeLabel,
} from './modal-utils';
import { renderModalBodyHtml } from './modal-render';
import { __ } from '@wordpress/i18n';

( function () {
	'use strict';

	const OPEN_CLASS = MODAL_OPEN_CLASS;
	let activeController = null;

	/**
	 * @param {HTMLElement} root Root element to search within.
	 * @return {HTMLElement[]} Focusable elements inside the root.
	 */
	function getFocusableElements( root ) {
		const selectors = [
			'a[href]',
			'button:not([disabled])',
			'textarea:not([disabled])',
			'input:not([disabled])',
			'select:not([disabled])',
			'[tabindex]:not([tabindex="-1"])',
		];

		return Array.from(
			root.querySelectorAll( selectors.join( ',' ) )
		).filter(
			( element ) =>
				! element.hasAttribute( 'disabled' ) &&
				element.getAttribute( 'aria-hidden' ) !== 'true'
		);
	}

	/**
	 * @param {HTMLElement} section Section root.
	 * @return {Object|null} Parsed modal section data or null when invalid.
	 */
	function readSectionData( section ) {
		const script = section.querySelector(
			'.alteia-mtm-modal-section__data'
		);

		if ( ! script?.textContent ) {
			return null;
		}

		try {
			return JSON.parse( script.textContent );
		} catch {
			return null;
		}
	}

	/**
	 * @param {KeyboardEvent} event Keyboard event.
	 */
	function onGlobalKeydown( event ) {
		if ( ! activeController ) {
			return;
		}

		activeController.handleKeydown( event );
	}

	document.addEventListener( 'keydown', onGlobalKeydown );

	/**
	 * @param {HTMLElement} section Section element.
	 */
	function initMediaTextModalSection( section ) {
		const sectionData = readSectionData( section );
		const modal = section.querySelector( '[data-mtm-modal-root="true"]' );

		if ( ! sectionData || ! modal ) {
			return;
		}

		const dialog = modal.querySelector( '.alteia-mtm-modal__dialog' );
		const inner = modal.querySelector( '.alteia-mtm-modal__inner' );
		const title = modal.querySelector( '.alteia-mtm-modal__title' );
		const body = modal.querySelector( '.alteia-mtm-modal__body' );
		const closeButton = modal.querySelector( '.alteia-mtm-modal__close' );
		const overlay = modal.querySelector( '.alteia-mtm-modal__overlay' );

		const closeDialogLabel = getModalChromeLabel(
			'closeDialog',
			__( 'Close dialog', 'alteia' )
		);
		const closeModalLabel = getModalChromeLabel(
			'closeModal',
			__( 'Close modal', 'alteia' )
		);

		if ( overlay ) {
			overlay.setAttribute( 'aria-label', closeDialogLabel );
		}

		if ( closeButton ) {
			closeButton.setAttribute( 'aria-label', closeModalLabel );
		}

		let lastTrigger = null;

		/**
		 * @param {boolean} isOpen Whether the modal is open.
		 */
		function setModalFocusability( isOpen ) {
			if ( overlay ) {
				overlay.tabIndex = isOpen ? 0 : -1;
			}

			if ( closeButton ) {
				closeButton.tabIndex = isOpen ? 0 : -1;
			}
		}

		/**
		 * @param {Object} cardData Card modal data.
		 */
		function populateModal( cardData ) {
			if ( title ) {
				title.textContent = decodeHtmlEntities(
					cardData.modalTitle || ''
				);
			}

			modal.classList.remove(
				'alteia-mtm-modal--fund',
				'alteia-mtm-modal--capital'
			);

			if ( cardData.modalType ) {
				modal.classList.add(
					`alteia-mtm-modal--${ cardData.modalType }`
				);
			}

			if ( cardData.modalAccentColor ) {
				modal.style.setProperty(
					'--alteia-mtm-modal-accent',
					cardData.modalAccentColor
				);
			} else {
				modal.style.removeProperty( '--alteia-mtm-modal-accent' );
			}

			const hoverColor =
				cardData.modalType === 'capital'
					? THEME_COLORS.primaryBlue
					: THEME_COLORS.primaryGreen;
			modal.style.setProperty(
				'--alteia-media-text-modal-section-button-hover',
				hoverColor
			);

			if ( body ) {
				body.innerHTML = renderModalBodyHtml( cardData );
			}
		}

		/**
		 * Close the modal after the leave transition completes.
		 */
		function closeModal() {
			if ( ! modal.classList.contains( OPEN_CLASS ) ) {
				return;
			}

			modal.setAttribute( 'aria-hidden', 'true' );
			setModalFocusability( false );
			unlockBackgroundScroll( { bodyClass: MODAL_BODY_CLASS } );

			if ( activeController === controller ) {
				activeController = null;
			}

			animateModalClose( modal, {
				openClass: OPEN_CLASS,
				onClosed() {
					modal.hidden = true;
					if ( body ) {
						body.innerHTML = '';
					}
					if (
						lastTrigger &&
						typeof lastTrigger.focus === 'function' &&
						document.contains( lastTrigger )
					) {
						lastTrigger.focus();
					}
					lastTrigger = null;
				},
			} );
		}

		/**
		 * @param {string}            cardId  Card id.
		 * @param {HTMLButtonElement} trigger Trigger button.
		 */
		function openModal( cardId, trigger ) {
			const cardData = findCardModalData( sectionData, cardId );

			if ( ! cardData || cardData.modalEnabled === false ) {
				return;
			}

			if (
				activeController &&
				activeController !== controller &&
				typeof activeController.close === 'function'
			) {
				activeController.close();
			}

			lastTrigger = trigger;
			populateModal( cardData );

			modal.hidden = false;
			modal.setAttribute( 'aria-hidden', 'false' );
			setModalFocusability( true );
			lockBackgroundScroll( {
				bodyClass: MODAL_BODY_CLASS,
				compensateScrollbar: true,
			} );
			resetModalScroll( modal );
			resetModalScroll( inner );
			resetModalScroll( body );

			activeController = controller;

			animateModalOpen( modal, {
				openClass: OPEN_CLASS,
				onOpened() {
					resetModalScroll( modal );
					resetModalScroll( inner );
					resetModalScroll( body );
					if ( closeButton ) {
						closeButton.focus();
					}
				},
			} );
		}

		/**
		 * @param {KeyboardEvent} event Keyboard event.
		 */
		function handleKeydown( event ) {
			if ( ! modal.classList.contains( OPEN_CLASS ) ) {
				return;
			}

			if ( event.key === 'Escape' ) {
				event.preventDefault();
				closeModal();
				return;
			}

			if ( event.key === 'Tab' && dialog ) {
				const focusables = getFocusableElements( dialog );
				if ( focusables.length === 0 ) {
					return;
				}

				const first = focusables[ 0 ];
				const last = focusables[ focusables.length - 1 ];
				const active = modal.ownerDocument.activeElement;

				if ( event.shiftKey && active === first ) {
					event.preventDefault();
					last.focus();
				} else if ( ! event.shiftKey && active === last ) {
					event.preventDefault();
					first.focus();
				}
			}
		}

		const controller = { close: closeModal, handleKeydown, modal };

		section.addEventListener( 'click', ( event ) => {
			const trigger = event.target.closest(
				'[data-modal-trigger="true"]'
			);

			if ( ! trigger || ! section.contains( trigger ) ) {
				return;
			}

			if (
				trigger.dataset.blockInstanceId &&
				sectionData.blockInstanceId &&
				trigger.dataset.blockInstanceId !== sectionData.blockInstanceId
			) {
				return;
			}

			event.preventDefault();
			openModal( trigger.dataset.cardId || '', trigger );
		} );

		modal.addEventListener( 'click', ( event ) => {
			if ( event.target.closest( '[data-mtm-modal-close="true"]' ) ) {
				event.preventDefault();
				closeModal();
			}
		} );

		if ( dialog ) {
			dialog.addEventListener( 'click', ( event ) => {
				if ( event.target.closest( '[data-mtm-modal-close="true"]' ) ) {
					event.preventDefault();
					closeModal();
					return;
				}

				event.stopPropagation();
			} );
		}

		if ( closeButton ) {
			closeButton.addEventListener( 'click', ( event ) => {
				event.preventDefault();
				closeModal();
			} );
		}

		setModalFocusability( false );
	}

	document.addEventListener( 'DOMContentLoaded', () => {
		document
			.querySelectorAll( '.alteia-media-text-modal-section' )
			.forEach( initMediaTextModalSection );
	} );
} )();
